import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useBulkImportTargetsMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { LegacyTokens, B13, B13Ink } from "../../../theme/b13Tokens";

/**
 * Batch 44 — target-level paste import.
 *
 * Matches the client's flow: one dialog, one textarea, one instruction
 * line, Cancel + Import. Targets are appended to the existing program in
 * paste order, continuing the existing numbering. New rows default to
 * `waiting` status and inherit prompt hierarchy / measurement / mastery
 * workflow from the program (null overrides on the target).
 *
 * Duplicate handling — the pasted names are cross-referenced against the
 * program's existing target names. When any duplicate is detected, the
 * dialog surfaces a non-blocking warning that names the collisions and
 * makes the count explicit; the user can proceed (creating true
 * duplicates) or cancel. The backend never silently dedupes; the client
 * never silently drops a paste. This is the "tell the user what will
 * happen and let them proceed" rule from the batch spec.
 */
type Props = {
  open: boolean;
  onClose: () => void;
  programId: number | string | null;
  existingNames: string[];
  onImported?: () => void;
};

export default function ImportTargetsDialog({ open, onClose, programId, existingNames, onImported }: Props) {
  const { showMessage, showErrorMessage } = useNotifier();
  const [bulkImport, { isLoading }] = useBulkImportTargetsMutation();
  const [text, setText] = useState("");

  // Parse once per keystroke: strip whitespace, drop empty lines.
  const parsed = useMemo(() => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean), [text]);

  // Case-insensitive, whitespace-normalised duplicate detection against
  // existing target names on this program. Kept client-side so the
  // warning appears live while the user pastes.
  const existingLookup = useMemo(() => {
    const s = new Set<string>();
    for (const n of existingNames) s.add(n.trim().toLowerCase());
    return s;
  }, [existingNames]);
  const duplicates = useMemo(
    () => parsed.filter((n) => existingLookup.has(n.toLowerCase())),
    [parsed, existingLookup],
  );

  const close = () => {
    setText("");
    onClose();
  };

  const run = async () => {
    if (!programId || parsed.length === 0) return;
    try {
      const res: any = await bulkImport({ programId, targets: parsed }).unwrap();
      if (res?.status) {
        showMessage(`Imported ${parsed.length} target${parsed.length === 1 ? "" : "s"}.`);
        onImported?.();
        close();
      } else {
        showErrorMessage(res?.message || "Import failed.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Import failed.");
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: LegacyTokens.ink }}>
        Import targets
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: B13Ink.ink3, mb: 2 }}>
          Paste one target per line. Blank lines and surrounding whitespace are ignored.
          New targets are added to the end of the list and inherit this program's prompt level template, measurement and mastery workflow.
        </Typography>
        <TextField
          multiline
          rows={10}
          fullWidth
          autoFocus
          placeholder={"Ch1 Adding and Subtracting Numbers Up to 100\nCh2 Working with Equal Groups of Objects\nCh3 Understanding Place Value"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: LegacyTokens.surface,
              borderRadius: `${LegacyTokens.radius.control}px`,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
            },
          }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: B13Ink.ink3 }}>
            {parsed.length === 0
              ? "0 targets"
              : `${parsed.length} target${parsed.length === 1 ? "" : "s"} will be added, continuing the existing numbering.`}
          </Typography>
        </Stack>
        {duplicates.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {duplicates.length} pasted name{duplicates.length === 1 ? "" : "s"} already exist{duplicates.length === 1 ? "s" : ""} on this program.
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              Proceeding will add a second target with the same name. Cancel and edit the paste if that isn't intended.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, "& li": { fontSize: 12 } }}>
              {duplicates.slice(0, 5).map((n, i) => <li key={i}>{n}</li>)}
              {duplicates.length > 5 && <li>…and {duplicates.length - 5} more</li>}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={run}
          disabled={isLoading || parsed.length === 0 || !programId}
          startIcon={<FileUploadIcon />}
          sx={{
            backgroundColor: B13.primary,
            "&:hover": { backgroundColor: B13.primary },
          }}
        >
          {isLoading ? "Importing…" : `Import ${parsed.length || ""}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
